import { Link } from "@tanstack/react-router";
import { XamseenLogo } from "@/components/xamseen-logo";
import { LEGAL, LEGAL_PAGES } from "@/lib/legal";
import { cn } from "@/lib/utils";

/**
 * Shared chrome for the public trust pages. Works signed in or signed out, so
 * a shared link never bounces a visitor to sign-in.
 */
export function LegalPage({
  title,
  intro,
  toc,
  children,
}: {
  title: string;
  intro: string;
  toc?: { id: string; label: string }[];
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-background/90 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-4xl items-center justify-between px-5">
          <Link to="/" aria-label="Xamseen home" className="flex items-center">
            <XamseenLogo className="h-5 w-auto" />
          </Link>
          <Link to="/" className="text-sm font-medium text-plum hover:underline">
            Open Xamseen
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 pb-16 pt-10">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Xamseen</p>
        <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h1>
        <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">{intro}</p>
        <p className="mt-4 text-xs text-muted-foreground">Last updated {LEGAL.lastUpdated}</p>

        {toc && toc.length > 0 && (
          <nav aria-label="On this page" className="mt-8 rounded-2xl border border-border bg-surface p-5">
            <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">On this page</h2>
            <ol className="mt-3 grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
              {toc.map((t, i) => (
                <li key={t.id} className="text-sm">
                  <a href={`#${t.id}`} className="text-foreground/85 hover:text-plum hover:underline">
                    <span className="mr-2 text-muted-foreground">{i + 1}.</span>
                    {t.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <div className="mt-10 space-y-10">{children}</div>
      </main>

      <LegalFooter />
    </div>
  );
}

export function Section({
  id,
  title,
  children,
  className,
}: {
  id?: string;
  title?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section id={id} className={cn("scroll-mt-20", className)}>
      {title && <h2 className="font-display text-xl font-semibold tracking-tight sm:text-[22px]">{title}</h2>}
      <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-foreground/85">{children}</div>
    </section>
  );
}

/** Bulleted list with comfortable line height for long-form reading. */
export function Bullets({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="space-y-2 pl-5">
      {items.map((item, i) => (
        <li key={i} className="list-disc marker:text-plum/70">
          {item}
        </li>
      ))}
    </ul>
  );
}

/** A quiet emphasis panel — no decorative iconography. */
export function Note({ tone = "neutral", children }: { tone?: "neutral" | "warn"; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        "rounded-2xl border p-4 text-sm leading-relaxed",
        tone === "warn" ? "border-ember/40 bg-ember/[0.07]" : "border-border bg-surface",
      )}
    >
      {children}
    </div>
  );
}

/**
 * Renders a value the owner still has to supply. Visible on purpose: an
 * unconfirmed legal fact must never read as a confirmed one.
 */
export function Tbd({ label }: { label: string }) {
  return (
    <span className="rounded-md border border-dashed border-ember/60 bg-ember/[0.08] px-1.5 py-0.5 text-[13px] font-medium text-ember">
      [to be provided before launch: {label}]
    </span>
  );
}

/** Prints a config value, or a clearly marked placeholder when it is unset. */
export function Value({ value, label }: { value: string | null; label: string }) {
  return value ? <>{value}</> : <Tbd label={label} />;
}

export function LegalFooter() {
  return (
    <footer className="border-t border-border">
      <div className="mx-auto max-w-4xl px-5 py-10">
        <div className="grid gap-6 sm:grid-cols-2">
          {LEGAL_PAGES.map((p) => (
            <Link key={p.to} to={p.to} className="group">
              <span className="block text-sm font-semibold group-hover:text-plum">{p.label}</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">{p.blurb}</span>
            </Link>
          ))}
        </div>
        <p className="mt-8 text-xs leading-relaxed text-muted-foreground">
          Xamseen — see it, share it, shape it. Xamseen is a community platform, not an emergency service. In an
          emergency, contact your local emergency number first.
        </p>
      </div>
    </footer>
  );
}
